/**
 * Cell coloring helpers for the compare table. A/B value cells reuse the single-mode
 * color logic; the Δ cell is colored by the SIGN of the change (+ green, − red, ~0 muted)
 * so the color always matches the number. Group headers get the single-mode stat accent.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { CSSProperties } from 'react';

import type { ColumnDef } from '../shared/constants';

function accentOf(col: ColumnDef): 'conv' | 'reg' | 'ftd' | undefined {
  return 'columnAccent' in col
    ? (col as { columnAccent?: 'conv' | 'reg' | 'ftd' }).columnAccent
    : undefined;
}

/** Value-cell class/style, matching StatsRow's single-mode coloring. */
export function metricCellStyle(
  col: ColumnDef,
  numVal: number | null,
): { className: string; style?: CSSProperties } {
  const accent = accentOf(col);
  if (accent && numVal != null && numVal > 0) {
    const varName =
      accent === 'conv'
        ? '--stat-conv-value'
        : accent === 'reg'
          ? '--stat-reg-value'
          : '--stat-ftd-value';
    return { className: 'font-medium', style: { color: `hsl(var(${varName}))` } };
  }
  const hint = col.colorHint;
  if (hint === 'money' && numVal != null) {
    if (numVal > 0) return { className: 'text-success font-medium' };
    if (numVal < 0) return { className: 'text-destructive font-medium' };
  } else if (hint === 'metric' && numVal != null && numVal > 0) {
    return { className: 'text-primary font-medium' };
  } else if (hint === 'negative' && numVal != null) {
    return { className: 'text-destructive font-medium' };
  }
  return { className: 'text-muted-foreground' };
}

/** Group-header accent (background tint + text), matching single-mode StatsTable headers. */
export function metricHeaderStyle(col: ColumnDef): CSSProperties | undefined {
  const accent = accentOf(col);
  if (accent) {
    return {
      backgroundColor: `hsl(var(--stat-${accent}-header))`,
      color: `hsl(var(--stat-${accent}-value))`,
    };
  }
  if (col.key === 'profit') {
    return {
      backgroundColor: 'hsl(var(--stat-profit-header))',
      color: 'hsl(var(--stat-profit-value))',
    };
  }
  if (col.key === 'cost') {
    return {
      backgroundColor: 'hsl(var(--stat-cost-header))',
      color: 'hsl(var(--stat-cost-value))',
    };
  }
  return undefined;
}

/** Δ color by sign so it always matches the number: + green, − red, ~0 muted. */
export function deltaToneClass(delta: number): string {
  if (Math.abs(delta) < 1e-9) return 'text-muted-foreground';
  return delta > 0 ? 'text-success' : 'text-destructive';
}
